<template>
  <div class="cancellation-order-summary h-100 p-4">
    <div class="eyebrow-label mb-2">
      {{ t("go_cancel.summary_order_title") }}
    </div>

    <div class="d-flex align-items-baseline gap-2 mb-3">
      <span
        class="font-serif summary-amount"
        :class="{ 'summary-amount--empty': !hasOrder }"
      >
        {{ formatMwh(mwhToCancel) }}
      </span>
      <span class="text-secondary">{{
        t("go_cancel.summary_mwh_to_cancel")
      }}</span>
    </div>

    <!-- Populated: live readout + readiness status -->
    <div v-if="hasOrder" class="border-top pt-3">
      <dl class="summary-readout mb-0">
        <div class="d-flex justify-content-between align-items-center gap-3">
          <dt class="text-secondary fw-normal">
            {{ t("go_cancel.summary_statements") }}
          </dt>
          <dd class="fw-semibold mb-0 tabular">{{ statementsCount }}</dd>
        </div>
        <div class="d-flex justify-content-between align-items-center gap-3">
          <dt class="text-secondary fw-normal">
            {{ t("go_cancel.form.beneficiary_type.label") }}
          </dt>
          <dd class="fw-semibold mb-0 text-end">{{ beneficiaryTypeLabel }}</dd>
        </div>
        <div class="d-flex justify-content-between align-items-center gap-3">
          <dt class="text-secondary fw-normal">
            {{ t("go_cancel.summary_consumption_period") }}
          </dt>
          <dd class="fw-semibold mb-0 tabular text-end">{{ periodLabel }}</dd>
        </div>
        <div class="d-flex justify-content-between align-items-center gap-3">
          <dt class="text-secondary fw-normal">
            {{ t("go_cancel.summary_certificates_selected") }}
          </dt>
          <dd class="fw-semibold mb-0 tabular">
            {{
              t("go_cancel.summary_mwh_value", { mwh: formatMwh(selectedMwh) })
            }}
          </dd>
        </div>
      </dl>

      <div
        class="summary-status d-flex align-items-center gap-2 mt-3 px-3 py-2 rounded-3"
        :class="statusClasses"
      >
        <FontAwesomeIcon :icon="statusIcon" class="flex-shrink-0" />
        <span class="small fw-medium">{{ status.label }}</span>
      </div>
    </div>

    <!-- Empty: turn the summary into an intentional next-steps guide -->
    <div v-else class="border-top pt-3">
      <div class="eyebrow-label eyebrow-label--sm mb-3">
        {{ t("go_cancel.summary_build_title") }}
      </div>
      <ol class="summary-steps list-unstyled mb-0 d-flex flex-column gap-3">
        <li
          v-for="(step, index) in buildSteps"
          :key="index"
          class="d-flex align-items-start gap-2"
        >
          <span class="summary-step-num flex-shrink-0">{{ index + 1 }}</span>
          <span>{{ step }}</span>
        </li>
      </ol>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatMwh } from "@/composables/formatUtils";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

export type OrderReadinessVariant = "ready" | "pending" | "warning";

const props = defineProps<{
  mwhToCancel: number;
  statementsCount: number;
  beneficiaryTypeLabel: string;
  periodLabel: string;
  selectedMwh: number;
  status: { label: string; variant: OrderReadinessVariant };
}>();

const { t } = useI18n();

const hasOrder = computed(() => props.mwhToCancel > 0);

const buildSteps = computed(() => [
  t("go_cancel.summary_step_beneficiary"),
  t("go_cancel.summary_step_certificates"),
  t("go_cancel.summary_step_review"),
]);

const statusClasses = computed(() => {
  switch (props.status.variant) {
    case "ready":
      return "bg-success-subtle text-success-emphasis border border-success-subtle";
    case "warning":
      return "bg-warning-subtle text-warning-emphasis border border-warning-subtle";
    default:
      return "bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle";
  }
});

const statusIcon = computed(() => {
  switch (props.status.variant) {
    case "ready":
      return "check-circle";
    case "warning":
      return "exclamation-triangle";
    default:
      return "arrow-right";
  }
});
</script>

<style scoped lang="scss">
.cancellation-order-summary {
  background: var(--phoenix-body-highlight-bg);
  border: 1px solid var(--phoenix-border-color);
  border-radius: 12px;
}

.summary-amount {
  font-size: 2.125rem;
  font-weight: 500;
  line-height: 1;
  letter-spacing: -0.015em;
  font-variant-numeric: tabular-nums;

  &--empty {
    color: var(--phoenix-quaternary-color);
  }
}

.summary-readout {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.8125rem;
}

.tabular {
  font-variant-numeric: tabular-nums;
}

.summary-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid
    var(--phoenix-border-color-strong, var(--phoenix-border-color));
  font-size: 11px;
  font-weight: 600;
  color: var(--phoenix-secondary-color);
}

.summary-steps {
  font-size: 0.8125rem;
}
</style>
